// CQ_AUTOADD_ROBUST_20260815
import {
  memo,
  useState,
  useEffect,
  useRef,
  useCallback
} from 'react';
import { downloadSyllabusPdfRoot } from '../../../utils/syllabusPdf';
import {
  buildAttendanceContributionTableHtml,
  normalizeAttendanceSettings
} from '../../../utils/attendanceCalculations';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const API_URL = process.env.REACT_APP_API_URL;


const StableRichText = memo(
  function StableRichText({
    html,
    style
  }) {
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: html
        }}
        style={style}
      />
    );
  }
);


const SYSTEM_ASSIGNMENT_TYPE_NAMES = new Set([
  'Attendance',
  'Critical Questions',
  'Questions Assignment',
  'Philosophical Diary',
  'Question Improvement'
]);

function isOrdinaryAssignmentType(type) {
  if (!type) return false;

  return (
    type.is_core !== true &&
    !SYSTEM_ASSIGNMENT_TYPE_NAMES.has(type.name)
  );
}

function normalizeQuizQuestions(value) {
  let rows = value;

  if (typeof rows === 'string') {
    try {
      rows = JSON.parse(rows);
    } catch {
      rows = [];
    }
  }

  if (!Array.isArray(rows)) {
    return [];
  }

  return [...rows].sort(
    (a, b) =>
      (Number(a.question_order) || 0) -
      (Number(b.question_order) || 0)
  );
}

function isCriticalQuestionsSection(section) {
  if (!section) return false;

  return (
    section.name === 'Critical Questions' ||
    section.name === 'Questions Assignment' ||
    section.has_clarificatory_variants === true
  );
}

function isAiPermissionsSection(section) {
  if (!section) return false;

  return (
    section.is_ai_permissions === true ||
    section.name === 'AI Permissions'
  );
}

function isAcademicIntegrityContractSection(section) {
  if (!section) return false;

  return section.name === 'Academic Integrity Contract';
}

function isSectionApplicable(section, course) {
  if (!section) return false;

  if (section.name === 'Attendance') {
    return course.attendance_enabled === true;
  }

  if (isCriticalQuestionsSection(section)) {
    return (
      course.cq_enabled === true ||
      course.qi_enabled === true
    );
  }

  if (section.name === 'Philosophical Diary') {
    return course.pd_enabled === true;
  }

  if (section.name === 'Question Improvement') {
    return course.qi_enabled === true;
  }

  return true;
}

async function fetchSections(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_syllabus_sections?course_id=eq.${courseId}&select=section_order,cooldown_minutes_override,quiz_questions_override,syllabus_sections(*,syllabus_quiz_questions(*))&order=section_order.asc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  const rows =
    await response.json();

  return Array.isArray(rows)
    ? rows
        .map(row => ({
          ...row.syllabus_sections,

          section_order:
            row.section_order,

          cooldown_minutes_override:
            row.cooldown_minutes_override,

          quiz_questions_override:
            row.quiz_questions_override
        }))
        .filter(Boolean)
    : [];
}

async function fetchCourseContent(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_syllabus_content?course_id=eq.${courseId}&select=*`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchCourseAssignments(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignments?course_id=eq.${courseId}&select=*,assignment_types(id,name,is_core,instructions,syllabus_cooldown_minutes,assignment_type_quiz_questions(*))&order=instance_number.asc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  return response.json();
}

async function fetchTypeDescriptions(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_descriptions?course_id=eq.${courseId}&select=*`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchTypeDisplayNames(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_settings?course_id=eq.${courseId}&select=assignment_type_id,display_name`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  return response.json();
}

async function fetchWeeks(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&select=*,course_week_content(description)&order=week_number.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchHolidays(instructorId) {
  if (!instructorId) return [];

  const instructorResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/instructors?id=eq.${instructorId}&select=institution_id&limit=1`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!instructorResponse.ok) {
    return [];
  }

  const instructorRows =
    await instructorResponse.json();

  const institutionId =
    instructorRows?.[0]?.institution_id ||
    null;

  if (!institutionId) {
    return [];
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/holidays?institution_id=eq.${institutionId}&or=(is_personal.eq.false,instructor_id.eq.${instructorId})&select=*&order=date.asc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    return [];
  }

  const data = await response.json();

  return Array.isArray(data)
    ? data
    : [];
}

async function fetchMeetings(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/student_meetings?course_id=eq.${courseId}&select=*&order=meeting_date.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchWeekReadings(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_week_reading_assignments?select=*,course_readings(*),course_weeks!inner(course_id)&course_weeks.course_id=eq.${courseId}&order=display_order.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchAiPermissions(courseId) {
  const headers = {
    'apikey': SUPABASE_KEY,
    'Authorization':
      'Bearer ' + SUPABASE_KEY
  };

  const [
    typesResponse,
    permissionsResponse
  ] =
    await Promise.all([
      fetch(
        `${SUPABASE_URL}/rest/v1/ai_use_types?select=id,label,description,ai_use_categories(name,display_order)&order=ai_use_categories(display_order).asc,label.asc`,
        { headers }
      ),

      fetch(
        `${SUPABASE_URL}/rest/v1/course_ai_permissions?course_id=eq.${courseId}&select=id,ai_use_type_id,permitted,note`,
        { headers }
      )
    ]);


  const types =
    await typesResponse.json();

  const permissions =
    await permissionsResponse.json();


  const permissionByType =
    new Map(
      (
        Array.isArray(
          permissions
        )
          ? permissions
          : []
      ).map(
        permission => [
          String(
            permission
              .ai_use_type_id
          ),

          permission
        ]
      )
    );


  return (
    Array.isArray(types)
      ? types
      : []
  ).map(type => {
    const permission =
      permissionByType.get(
        String(
          type.id
        )
      );


    return {
      id:
        permission?.id ||
        'ai-type-' + type.id,

      ai_use_type_id:
        type.id,

      permitted:
        permission
          ?.permitted ===
        true,

      note:
        permission?.note ||
        null,

      ai_use_types:
        type
    };
  });
}

async function fetchAttendanceWeight(courseId) {
  const headers = {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY
  };

  const typeResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_types?name=eq.Attendance&select=id&limit=1`,
    { headers }
  );
  const types = await typeResponse.json();
  const attendanceTypeId = Array.isArray(types) ? types[0]?.id : null;

  if (!attendanceTypeId) return 0;

  const weightResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_weights?course_id=eq.${courseId}&assignment_type_id=eq.${attendanceTypeId}&select=weight_percent&limit=1`,
    { headers }
  );
  const weights = await weightResponse.json();

  return Number(Array.isArray(weights) ? weights[0]?.weight_percent : 0) || 0;
}

async function fetchAttendanceRequiredWeekIds(enrolmentId, courseId) {
  const headers = {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY
  };

  const membershipResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance_group_memberships?enrolment_id=eq.${enrolmentId}&select=attendance_group_id,attendance_groups!inner(course_id)&attendance_groups.course_id=eq.${courseId}&limit=1`,
    { headers }
  );
  const memberships = await membershipResponse.json();
  const membership = Array.isArray(memberships) ? memberships[0] : null;

  if (!membership?.attendance_group_id) return [];

  const weeksResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance_group_weeks?attendance_group_id=eq.${membership.attendance_group_id}&select=course_week_id`,
    { headers }
  );
  const rows = await weeksResponse.json();

  return (Array.isArray(rows) ? rows : [])
    .map(row => row.course_week_id)
    .filter(Boolean);
}

async function markSyllabusComplete(studentId, courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/student_course_onboarding?student_id=eq.${studentId}&course_id=eq.${courseId}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        syllabus_completed: true,
        syllabus_completed_at: new Date().toISOString()
      })
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Saving syllabus completion failed (${response.status}): ${detail}`
    );
  }
}

async function markContractComplete(
  studentId,
  courseId,
  signedName
) {
  const completedAt = new Date().toISOString();

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/student_course_onboarding?student_id=eq.${studentId}&course_id=eq.${courseId}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        contract_completed: true,
        contract_completed_at: completedAt,
        contract_name_signed: signedName
      })
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Saving Academic Integrity Contract signature failed (${response.status}): ${detail}`
    );
  }

  return completedAt;
}

function substituteVariables(content, course, attendanceTableHtml = '') {
  if (!content) return '';

  let result = content
    .replace(/\{\{course_name\}\}/g, course.course_titles?.name || course.name || '')
    .replace(/\{\{semester\}\}/g, course.semesters?.name || '')
    .replace(/\{\{class_day\}\}/g, course.class_day || '')
    .replace(/\{\{class_time\}\}/g, (() => {
      const times = course.class_times;
      const days = course.class_days;
      if (times && days?.length) return days.map(d => times[d] ? times[d].slice(0,5) : '').filter(Boolean).join(' / ');
      return course.class_time?.slice(0, 5) || '';
    })())
    .replace(/\{\{pd_due_day\}\}/g, course.pd_due_day || '')
    .replace(/\{\{start_date\}\}/g, course.semesters?.start_date || '')
    .replace(/\{\{mid_eval_close\}\}/g, course.mid_eval_close_date || '')
    .replace(/\{\{final_eval_close\}\}/g, course.final_eval_close_date || '')
    .replace(/\{\{cq_clarificatory_allowed\}\}/g, course.cq_clarificatory_allowed?.toString() || '0');

  // Rich-text editors commonly wrap a variable on its own line in <p> tags.
  // Replace that whole paragraph first so the generated table is not nested
  // inside invalid paragraph markup.
  result = result
    .replace(
      /<p>\s*\{\{attendance_calculation_table\}\}\s*<\/p>/gi,
      attendanceTableHtml
    )
    .replace(
      /\{\{attendance_calculation_table\}\}/g,
      attendanceTableHtml
    );

  return result;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });
}

function stripHtml(html) {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
}


function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function safePdfFilePart(value) {
  return String(value || '')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };

    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}


function getSyllabusCalendarWeekStart(
  dateStr
) {
  if (!dateStr) return '';

  const parts = String(dateStr)
    .slice(0, 10)
    .split('-')
    .map(Number);

  if (
    parts.length !== 3 ||
    !parts.every(Number.isFinite)
  ) {
    return '';
  }

  const date = new Date(
    parts[0],
    parts[1] - 1,
    parts[2],
    12,
    0,
    0
  );

  const dayOfWeek =
    date.getDay();

  date.setDate(
    date.getDate() -
      (
        dayOfWeek === 0
          ? 6
          : dayOfWeek - 1
      )
  );

  return [
    date.getFullYear(),
    String(
      date.getMonth() + 1
    ).padStart(2, '0'),
    String(
      date.getDate()
    ).padStart(2, '0')
  ].join('-');
}


function isFirstActualSyllabusClassOfWeek(
  week,
  weeks,
  holidays
) {
  const weekStart =
    getSyllabusCalendarWeekStart(
      week.class_date
    );

  const sameWeek = weeks
    .filter(
      candidate =>
        getSyllabusCalendarWeekStart(
          candidate.class_date
        ) === weekStart
    )
    .slice()
    .sort(
      (a, b) =>
        String(
          a.class_date || ''
        ).localeCompare(
          String(
            b.class_date || ''
          )
        )
    );

  const firstActualClass =
    sameWeek.find(
      candidate =>
        !holidays.some(
          holiday =>
            holiday.date ===
            candidate.class_date
        )
    ) ||
    sameWeek[0];

  if (!firstActualClass) {
    return false;
  }

  if (
    firstActualClass.id &&
    week.id
  ) {
    return (
      firstActualClass.id ===
      week.id
    );
  }

  return (
    firstActualClass.class_date ===
    week.class_date
  );
}

function buildTeachingSchedulePrintHtml({
  weeks,
  holidays,
  meetings,
  courseAssignments,
  weekReadings,
  typeDisplayNames
}) {
  function getHoliday(date) {
    return holidays.find(holiday => holiday.date === date);
  }

  function getMeetingsForWeek(week) {
    const classDate = new Date(week.class_date);
    const dayOfWeek = classDate.getDay();
    const monday = new Date(classDate);
    monday.setDate(
      classDate.getDate() -
      (dayOfWeek === 0 ? 6 : dayOfWeek - 1)
    );
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    return meetings.filter(meeting => {
      const date = new Date(meeting.meeting_date);
      return date >= monday && date <= sunday;
    });
  }

  function getAssignmentsDue(week) {
    const classDate = new Date(week.class_date);
    const dayOfWeek = classDate.getDay();
    const monday = new Date(classDate);
    monday.setDate(
      classDate.getDate() -
      (dayOfWeek === 0 ? 6 : dayOfWeek - 1)
    );
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    return courseAssignments.filter(assignment => {
      if (assignment.assignment_types?.is_core) return false;

      const due =
        assignment.due_date ||
        assignment.occurs_on;

      if (!due) return false;

      const date = new Date(due);
      return date >= monday && date <= sunday;
    });
  }

  function getWeekReadingsList(weekId) {
    return weekReadings
      .filter(reading => reading.course_week_id === weekId)
      .sort((a, b) => a.display_order - b.display_order);
  }

  return `
    <div class="print-schedule">
      ${weeks.map(week => {
        const holiday = getHoliday(week.class_date);
        const weekMeetings = getMeetingsForWeek(week);
        const dueAssignments =
          isFirstActualSyllabusClassOfWeek(
            week,
            weeks,
            holidays
          )
            ? getAssignmentsDue(week)
            : [];
        const readings = getWeekReadingsList(week.id);
        const description = Array.isArray(week.course_week_content)
          ? week.course_week_content[0]?.description
          : week.course_week_content?.description;

        const items = [
          ...(holiday ? [{
            type: 'holiday',
            date: week.class_date,
            text: `No Class: ${holiday.reason || ''}`
          }] : []),
          ...weekMeetings.map(meeting => ({
            type: 'meeting',
            date: meeting.meeting_date,
            text:
              `${meeting.meeting_type || ''} Self-Evaluation Meetings: ` +
              formatDate(meeting.meeting_date)
          })),
          ...dueAssignments.map(assignment => {
            const requiresSubmission =
              assignment.assignment_types?.requires_submission ?? true;
            const label =
              typeDisplayNames?.[assignment.assignment_type_id] ||
              assignment.assignment_types?.name ||
              'Assignment';

            return {
              type: 'assignment',
              date:
                assignment.due_date ||
                assignment.occurs_on,
              text: requiresSubmission
                ? `${label} due ${formatDate(
                    assignment.due_date ||
                    assignment.occurs_on
                  )}`
                : label
            };
          })
        ].sort(
          (a, b) =>
            new Date(a.date) -
            new Date(b.date)
        );

        return `
          <div class="print-schedule-row">
            <div class="print-schedule-week">
              <strong>W${escapeHtml(week.week_number)}</strong>
              <div>${escapeHtml(formatDate(week.class_date))}</div>
            </div>
            <div class="print-schedule-body">
              ${items.map(item => `
                <div class="print-schedule-item print-${item.type}">
                  ${escapeHtml(item.text)}
                </div>
              `).join('')}
              ${description
                ? `<div class="print-schedule-description">${escapeHtml(
                    stripHtml(description)
                  )}</div>`
                : ''}
              ${readings.map(readingAssignment => {
                const reading = readingAssignment.course_readings;
                if (!reading) return '';
                const title = reading.title || 'Reading';
                const author = reading.author ? ` by ${reading.author}` : '';
                return `<div class="print-schedule-reading"><strong>Reading:</strong> ${escapeHtml(title + author)}</div>`;
              }).join('')}
            </div>
          </div>
        `;
      }).join('')}
    </div>
  `;
}

function buildAiPermissionsPrintHtml(aiPermissions) {
  const permitted = aiPermissions.filter(
    permission => permission.permitted
  );

  const notPermitted = aiPermissions.filter(
    permission => !permission.permitted
  );

  return `
    <div class="print-ai-permissions">
      ${permitted.length > 0
        ? `
          <h3>Permitted</h3>
          ${permitted.map(permission => `
            <div class="print-ai-permission">
              <strong>${escapeHtml(
                permission.ai_use_types?.label ||
                'Permitted AI use'
              )}</strong>
              ${permission.ai_use_types?.description
                ? `<div>${escapeHtml(
                    permission.ai_use_types.description
                  )}</div>`
                : ''}
              ${permission.note
                ? `<div class="print-note">Course note: ${escapeHtml(
                    permission.note
                  )}</div>`
                : ''}
            </div>
          `).join('')}
        `
        : '<div>No AI uses are currently permitted.</div>'}

      ${notPermitted.length > 0
        ? `
          <h3>Not Permitted</h3>
          ${notPermitted.map(permission => `
            <div class="print-ai-permission">
              <strong>${escapeHtml(
                permission.ai_use_types?.label ||
                'AI use'
              )}</strong>
              ${permission.ai_use_types?.description
                ? `<div>${escapeHtml(
                    permission.ai_use_types.description
                  )}</div>`
                : ''}
              ${permission.note
                ? `<div class="print-note">Course note: ${escapeHtml(
                    permission.note
                  )}</div>`
                : ''}
            </div>
          `).join('')}
        `
        : ''}
    </div>
  `;
}

function TeachingSchedule({ course, weeks, holidays, meetings, courseAssignments, weekReadings }) {
  function getHoliday(date) {
    return holidays.find(h => h.date === date);
  }
  function getMeetingsForWeek(week) {
    const classDate = new Date(week.class_date);
    const dayOfWeek = classDate.getDay();
    const monday = new Date(classDate);
    monday.setDate(classDate.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return meetings.filter(m => {
      const d = new Date(m.meeting_date);
      return d >= monday && d <= sunday;
    });
  }
  function getAssignmentsDue(week) {
    const classDate = new Date(week.class_date);
    const dayOfWeek = classDate.getDay();
    const monday = new Date(classDate);
    monday.setDate(classDate.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return courseAssignments.filter(a => {
      if (a.assignment_types?.is_core) return false;
      const due = a.due_date || a.occurs_on;
      if (!due) return false;
      const d = new Date(due);
      return d >= monday && d <= sunday;
    });
  }

  function getWeekReadingsList(weekId) {
    return weekReadings
      .filter(reading => reading.course_week_id === weekId)
      .sort((a, b) => a.display_order - b.display_order);
  }

  return (
    <div style={scheduleStyles.wrap}>
      {weeks.map(week => {
        const holiday = getHoliday(week.class_date);
        const weekMeetings = getMeetingsForWeek(week);
        const dueAssignments =
          isFirstActualSyllabusClassOfWeek(
            week,
            weeks,
            holidays
          )
            ? getAssignmentsDue(week)
            : [];
        const readings = getWeekReadingsList(week.id);
        const description = Array.isArray(week.course_week_content)
          ? week.course_week_content[0]?.description
          : week.course_week_content?.description;

        const datedItems = [
          ...(holiday ? [{
            type: 'holiday',
            date: week.class_date,
            key: `holiday-${week.id}`,
            item: holiday
          }] : []),
          ...weekMeetings.map(m => ({
            type: 'meeting',
            date: m.meeting_date,
            key: `meeting-${m.id}`,
            item: m
          })),
          ...dueAssignments.map(a => ({
            type: 'assignment',
            date: a.due_date || a.occurs_on,
            key: `assignment-${a.id}`,
            item: a
          }))
        ].sort((a, b) => new Date(a.date) - new Date(b.date));

        return (
          <div key={week.id} style={scheduleStyles.weekRow}>
            <div style={scheduleStyles.weekLeft}>
              <div style={scheduleStyles.weekNum}>W{week.week_number}</div>
              <div style={scheduleStyles.weekDate}>{formatDate(week.class_date)}</div>
            </div>
            <div style={scheduleStyles.weekRight}>
              {datedItems.map(entry => {
                if (entry.type === 'holiday') {
                  return (
                    <div key={entry.key} style={scheduleStyles.holiday}>
                      No Class: {entry.item.reason}
                    </div>
                  );
                }

                if (entry.type === 'meeting') {
                  const m = entry.item;
                  return (
                    <div key={entry.key} style={scheduleStyles.meeting}>
                      {m.meeting_type} Self-Evaluation Meetings: {formatDate(m.meeting_date)}
                    </div>
                  );
                }

                if (entry.type === 'assignment') {
                  const a = entry.item;
                  const requiresSubmission = a.assignment_types?.requires_submission ?? true;
                  const label = a.assignment_types?.name || 'Assignment';
                  return (
                    <div key={entry.key} style={scheduleStyles.due}>
                      {requiresSubmission
                        ? `${label} due ${formatDate(a.due_date || a.occurs_on)}`
                        : label}
                    </div>
                  );
                }

                return null;
              })}
              {description && (
                <div style={scheduleStyles.description}>
                  {stripHtml(description)}
                </div>
              )}
              {readings.map(readingAssignment => {
                const reading = readingAssignment.course_readings;
                if (!reading) return null;

                const label = (
                  <>
                    <strong>Reading:</strong>{' '}
                    {reading.title || 'Reading'}
                    {reading.author ? ` by ${reading.author}` : ''}
                  </>
                );

                return reading.drive_file_id ? (
                  <a
                    key={readingAssignment.id}
                    href={`https://drive.google.com/file/d/${reading.drive_file_id}/preview`}
                    target="_blank"
                    rel="noreferrer"
                    style={scheduleStyles.readingLink}
                  >
                    {label}
                  </a>
                ) : (
                  <div key={readingAssignment.id} style={scheduleStyles.reading}>
                    {label}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AiPermissionsList({ aiPermissions }) {
  const permitted =
    aiPermissions.filter(
      permission =>
        permission.permitted ===
        true
    );

  const notPermitted =
    aiPermissions.filter(
      permission =>
        permission.permitted !==
        true
    );


  function groupByCategory(
    items
  ) {
    const grouped = {};


    items.forEach(
      permission => {
        const catName =
          permission
            .ai_use_types
            ?.ai_use_categories
            ?.name ||
          'Other';


        if (!grouped[catName]) {
          grouped[catName] = [];
        }


        grouped[catName].push(
          permission
        );
      }
    );


    return grouped;
  }


  function renderCategories(
    items
  ) {
    const grouped =
      groupByCategory(
        items
      );


    return Object
      .entries(grouped)
      .map(
        ([
          category,
          permissions
        ]) => (
          <div
            key={category}
            style={
              styles.aiCategory
            }
          >
            <div
              style={
                styles.aiCategoryName
              }
            >
              {category}
            </div>


            {permissions.map(
              permission => {
                const label =
                  permission
                    .ai_use_types
                    ?.label ||
                  'AI use';


                const description =
                  permission
                    .ai_use_types
                    ?.description ||
                  '';


                return (
                  <div
                    key={
                      permission.id
                    }
                    style={
                      styles
                        .aiPermissionItem
                    }
                  >
                    <div
                      style={
                        styles.aiType
                      }
                    >
                      • {label}
                    </div>


                    {description && (
                      <div
                        style={
                          styles
                            .aiDescription
                        }
                      >
                        {
                          description
                        }
                      </div>
                    )}
                  </div>
                );
              }
            )}
          </div>
        )
      );
  }


  return (
    <div
      style={
        styles.aiPermissions
      }
    >
      <div
        style={
          styles.aiSection
        }
      >
        <div
          style={
            styles.aiHeading
          }
        >
          ✓ Permitted
        </div>


        {permitted.length >
        0 ? (
          renderCategories(
            permitted
          )
        ) : (
          <div
            style={
              styles.muted
            }
          >
            No AI uses are currently permitted.
          </div>
        )}
      </div>


      <div
        style={
          styles.aiSection
        }
      >
        <div
          style={{
            ...styles.aiHeading,
            color:
              '#a12a2a'
          }}
        >
          ✕ Not Permitted
        </div>


        {notPermitted.length >
        0 ? (
          renderCategories(
            notPermitted
          )
        ) : (
          <div
            style={
              styles.muted
            }
          >
            No AI uses are currently prohibited.
          </div>
        )}
      </div>
    </div>
  );
}

export default function StudentSyllabus({
  session,
  course,
  enrolment,
  onboarding,
  onOnboardingUpdate,
  reviewBackRef = null
}) {
  const [sections, setSections] = useState([]);
  const [courseContent, setCourseContent] = useState({});
  const [courseAssignments, setCourseAssignments] = useState([]);
  const [typeDescriptions, setTypeDescriptions] = useState({});
  const [typeDisplayNames, setTypeDisplayNames] = useState({});
  const [typeOrders, setTypeOrders] = useState({});
  const [
  typeCooldownOverrides,
  setTypeCooldownOverrides
] = useState({});

const [
  typeQuizOverrides,
  setTypeQuizOverrides
] = useState({});
  const [weeks, setWeeks] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [weekReadings, setWeekReadings] = useState([]);
  const [aiPermissions, setAiPermissions] = useState([]);
  const [attendanceWeight, setAttendanceWeight] = useState(0);
  const [attendanceRequiredWeekIds, setAttendanceRequiredWeekIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizError, setQuizError] = useState('');
  const [selectedReviewSection, setSelectedReviewSection] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [contractNameInput, setContractNameInput] = useState('');
  const [contractSaving, setContractSaving] = useState(false);
  const [contractError, setContractError] = useState('');
  const [contractSigned, setContractSigned] = useState(
    onboarding?.contract_completed === true
  );
  const [contractSignedName, setContractSignedName] = useState(
    onboarding?.contract_name_signed || ''
  );
  const [contractSignedAt, setContractSignedAt] = useState(
    onboarding?.contract_completed_at || null
  );
  const timerRef = useRef(null);
  const walkthroughTopRef = useRef(null);

  const isReview = session?.isMaster || onboarding?.syllabus_completed || false;

  const studentName =
    session?.englishName ||
    session?.chineseName ||
    session?.studentCode ||
    '';

  useEffect(() => {
    setContractSigned(
      onboarding?.contract_completed === true
    );
    setContractSignedName(
      onboarding?.contract_name_signed || ''
    );
    setContractSignedAt(
      onboarding?.contract_completed_at || null
    );
    setContractNameInput('');
    setContractError('');
  }, [
    course.id,
    onboarding?.contract_completed,
    onboarding?.contract_name_signed,
    onboarding?.contract_completed_at
  ]);

  useEffect(() => {
    Promise.all([
      fetchSections(course.id),
      fetchCourseContent(course.id),
      fetchCourseAssignments(course.id),
      fetchTypeDescriptions(course.id),
      fetchTypeDisplayNames(course.id),
      fetchWeeks(course.id),
      fetchHolidays(course.instructor_id),
      fetchMeetings(course.id),
      fetchWeekReadings(course.id),
      fetchAiPermissions(course.id),
      fetchAttendanceWeight(course.id),
      course.group_based_attendance
        ? fetchAttendanceRequiredWeekIds(enrolment.id, course.id)
        : Promise.resolve([])
    ]).then(([s, cc, ca, td, tdn, w, h, m, wr, ai, attendanceWeightValue, attendanceWeekIds]) => {
      setSections(
        Array.isArray(s) ? s : []
      );

      const contentMap = {};
      (Array.isArray(cc) ? cc : []).forEach(c => {
        contentMap[c.syllabus_section_id] = c.content;
      });
      setCourseContent(contentMap);

      setCourseAssignments(
        Array.isArray(ca) ? ca : []
      );

      const descMap = {};
      const orderMap = {};
      const cooldownMap = {};
      const quizMap = {};

      (Array.isArray(td) ? td : []).forEach(d => {
        const typeId =
          d.assignment_type_id;

        descMap[typeId] =
          d.description;

        if (
          d.section_order !== null &&
          d.section_order !== undefined
        ) {
          orderMap[typeId] =
            Number(d.section_order);
        }

        cooldownMap[typeId] =
          d.syllabus_cooldown_minutes === null ||
          d.syllabus_cooldown_minutes === undefined
            ? null
            : Number(
                d.syllabus_cooldown_minutes
              );

        quizMap[typeId] =
          d.syllabus_quiz_questions === null ||
          d.syllabus_quiz_questions === undefined
            ? null
            : normalizeQuizQuestions(
                d.syllabus_quiz_questions
              );
      });

      setTypeDescriptions(descMap);

      const displayNameMap = {};
      (Array.isArray(tdn) ? tdn : []).forEach(row => {
        const value = String(row.display_name || '').trim();

        if (value) {
          displayNameMap[row.assignment_type_id] = value;
        }
      });
      setTypeDisplayNames(displayNameMap);

      setTypeOrders(orderMap);
      setTypeCooldownOverrides(cooldownMap);
      setTypeQuizOverrides(quizMap);

      setWeeks(
        Array.isArray(w) ? w : []
      );
      setHolidays(
        Array.isArray(h) ? h : []
      );
      setMeetings(
        Array.isArray(m) ? m : []
      );
      setWeekReadings(
        Array.isArray(wr) ? wr : []
      );
      setAiPermissions(
        Array.isArray(ai) ? ai : []
      );
      setAttendanceWeight(
        Number(attendanceWeightValue) || 0
      );
      setAttendanceRequiredWeekIds(
        Array.isArray(attendanceWeekIds)
          ? attendanceWeekIds
          : []
      );
    }).finally(() => setLoading(false));
  }, [
    course.id,
    course.instructor_id,
    course.group_based_attendance,
    enrolment.id
  ]);

  const attendanceRequiredCount = course.group_based_attendance
    ? attendanceRequiredWeekIds.length
    : weeks.filter(week => week.attendance_enabled).length;

  const attendanceSettings = normalizeAttendanceSettings(course);

  const attendanceTableHtml =
    course.grading_mode === 'traditional' &&
    course.attendance_enabled !== false
      ? buildAttendanceContributionTableHtml({
          totalRequired: attendanceRequiredCount,
          weight: attendanceWeight,
          ...attendanceSettings
        })
      : '';

  const getDisplaySections = useCallback(() => {
    const result = [];
    const seenTypes = new Set();

    const attachedSections = [...sections]
      .filter(
        section =>
          section.name !== 'Other Assignments' &&
          isSectionApplicable(section, course)
      )
      .sort((a, b) => {
        const aOrder = Number(a.section_order);
        const bOrder = Number(b.section_order);
        const safeA =
          Number.isFinite(aOrder)
            ? aOrder
            : Number.MAX_SAFE_INTEGER;
        const safeB =
          Number.isFinite(bOrder)
            ? bOrder
            : Number.MAX_SAFE_INTEGER;

        return safeA - safeB;
      });

    let pdOrder = null;

    for (const section of attachedSections) {
      const sectionOrder =
        Number(section.section_order);

      if (
        section.name ===
        'Philosophical Diary'
      ) {
        pdOrder =
          Number.isFinite(sectionOrder)
            ? sectionOrder
            : 0;
      }

      const defaultCooldown =
        Number(
          section.cooldown_minutes ??
          0
        );

      const effectiveCooldown =
        section.cooldown_minutes_override === null ||
        section.cooldown_minutes_override === undefined
          ? defaultCooldown
          : Number(
              section.cooldown_minutes_override
            );

      const defaultQuizQuestions =
        normalizeQuizQuestions(
          section.syllabus_quiz_questions
        );

      const effectiveQuizQuestions =
        section.quiz_questions_override === null ||
        section.quiz_questions_override === undefined
          ? defaultQuizQuestions
          : normalizeQuizQuestions(
              section.quiz_questions_override
            );

      result.push({
        ...section,
        section_order:
          Number.isFinite(sectionOrder)
            ? sectionOrder
            : 0,
        cooldown_minutes:
          effectiveCooldown,
        has_quiz:
          effectiveQuizQuestions.length > 0,
        syllabus_quiz_questions:
          effectiveQuizQuestions,
        isAssignment: false,
        isTypeDesc: false
      });
    }

    const uniqueAssignmentTypes = [];

    for (const assignment of courseAssignments) {
      if (
        !isOrdinaryAssignmentType(
          assignment.assignment_types
        )
      ) {
        continue;
      }

      const typeId =
        assignment.assignment_type_id;

      if (
        seenTypes.has(typeId) ||
        !Object.prototype.hasOwnProperty.call(
          typeDescriptions,
          typeId
        )
      ) {
        continue;
      }

      seenTypes.add(typeId);

      uniqueAssignmentTypes.push(
        assignment
      );
    }

    const fallbackBase =
      pdOrder !== null
        ? pdOrder + 0.5
        : result.reduce(
            (max, entry) =>
              Math.max(
                max,
                Number(entry.section_order) || 0
              ),
            0
          ) + 10;

    uniqueAssignmentTypes.forEach(
      (assignment, index) => {
        const typeId =
          assignment.assignment_type_id;

        const storedOrder =
          Number(typeOrders[typeId]);

        const globalQuizQuestions =
          normalizeQuizQuestions(
            assignment.assignment_types
              ?.assignment_type_quiz_questions
          );

        const quizOverride =
          Object.prototype.hasOwnProperty.call(
            typeQuizOverrides,
            typeId
          )
            ? typeQuizOverrides[typeId]
            : null;

        const quizQuestions =
          quizOverride === null ||
          quizOverride === undefined
            ? globalQuizQuestions
            : normalizeQuizQuestions(
                quizOverride
              );

        const globalCooldown =
          assignment.assignment_types
            ?.syllabus_cooldown_minutes ?? 1;

        const cooldownOverride =
          Object.prototype.hasOwnProperty.call(
            typeCooldownOverrides,
            typeId
          )
            ? typeCooldownOverrides[typeId]
            : null;

        const cooldownMinutes =
          cooldownOverride === null ||
          cooldownOverride === undefined
            ? Number(globalCooldown)
            : Number(cooldownOverride);

        result.push({
          id: `type-${typeId}`,
          name:
            typeDisplayNames[typeId] ||
            assignment.assignment_types
              ?.name ||
            'Assignment',
          section_order:
            Number.isFinite(storedOrder)
              ? storedOrder
              : fallbackBase +
                  (index * 0.001),
          cooldown_minutes:
            cooldownMinutes,
          has_quiz:
            quizQuestions.length > 0,
          syllabus_quiz_questions:
            quizQuestions,
          is_variable: false,
          is_teaching_schedule:
            false,
          is_ai_permissions: false,
          has_clarificatory_variants:
            false,
          isAssignment: true,
          isTypeDesc: true,
          typeId,
          defaultContent:
            assignment.assignment_types
              ?.instructions ||
            ''
        });
      }
    );

    return result.sort((a, b) => {
      const aOrder = Number(a.section_order);
      const bOrder = Number(b.section_order);
      const safeA =
        Number.isFinite(aOrder)
          ? aOrder
          : Number.MAX_SAFE_INTEGER;
      const safeB =
        Number.isFinite(bOrder)
          ? bOrder
          : Number.MAX_SAFE_INTEGER;

      return safeA - safeB;
    });
  }, [
    sections,
    courseAssignments,
    typeDescriptions,
    typeDisplayNames,
    typeOrders,
    typeCooldownOverrides,
    typeQuizOverrides,
    course
  ]);

  function getSectionContent(section) {
    if (section.isTypeDesc) {
      return (
        typeDescriptions[section.typeId] ||
        section.defaultContent ||
        ''
      );
    }
    if (section.is_teaching_schedule) return null;

    if (section.has_clarificatory_variants) {
      return course.cq_clarificatory_enabled
        ? (
            courseContent[`${section.id}_with`] ??
            section.content_with_clarificatory ??
            ''
          )
        : (
            courseContent[`${section.id}_without`] ??
            section.content_without_clarificatory ??
            ''
          );
    }

    return (
      courseContent[section.id] ??
      section.content ??
      ''
    );
  }

  const startCooldown = useCallback((minutes) => {
    if (!minutes || isReview) return;
    const seconds = Math.round(minutes * 60);
    setCooldownRemaining(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldownRemaining(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, [isReview]);

  useEffect(() => {
    if (!isReview) {
      const displaySections = getDisplaySections();

      if (displaySections[currentIndex]) {
        startCooldown(
          displaySections[currentIndex]
            .cooldown_minutes
        );
        setQuizAnswers({});
        setQuizError('');
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [
    currentIndex,
    getDisplaySections,
    isReview,
    startCooldown
  ]);

  useEffect(() => {
    if (isReview) return;

    const frame = window.requestAnimationFrame(() => {
      walkthroughTopRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'start'
      });
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [currentIndex, isReview]);

  function buildPrintableSectionHtml(section) {
    const content = getSectionContent(section);

    const bodyHtml = section.is_teaching_schedule
      ? buildTeachingSchedulePrintHtml({
          weeks,
          holidays,
          meetings,
          courseAssignments,
          weekReadings,
          typeDisplayNames
        })
      : `
          ${content
            ? `<div class="print-rich-text">${
                substituteVariables(content, course, attendanceTableHtml)
              }</div>`
            : ''}
          ${isAiPermissionsSection(section)
            ? buildAiPermissionsPrintHtml(aiPermissions)
            : ''}
        `;

    return `
      <section class="print-section">
        <h2>${escapeHtml(section.name)}</h2>
        ${bodyHtml}
      </section>
    `;
  }

  function createPrintableRoot(sectionsToPrint, wholeSyllabus) {
    const root = document.createElement('div');
    root.setAttribute('data-syllabus-print-root', 'true');

    Object.assign(root.style, {
      display: 'block',
      width: '760px',
      padding: '34px 42px',
      background: '#ffffff',
      color: '#243b53',
      fontFamily: 'Arial, sans-serif',
      fontSize: '14px',
      lineHeight: '1.55',
      boxSizing: 'border-box'
    });

    const courseName =
      course.name ||
      course.course_titles?.name ||
      'Course';

    root.innerHTML = `
      <style>
        [data-syllabus-print-root] h1 {
          font-size: 25px;
          margin: 0 0 8px;
          line-height: 1.25;
        }
        [data-syllabus-print-root] .print-subtitle {
          color: #627d98;
          margin-bottom: 26px;
          font-size: 13px;
        }
        [data-syllabus-print-root] .print-section {
          margin: 0 0 30px;
          break-inside: auto;
        }
        [data-syllabus-print-root] .print-section h2 {
          font-size: 19px;
          margin: 0 0 12px;
          padding-bottom: 6px;
          border-bottom: 1px solid #d9e2ec;
          break-after: avoid;
        }
        [data-syllabus-print-root] .print-rich-text p {
          margin: 0 0 10px;
        }
        [data-syllabus-print-root] .print-rich-text ul,
        [data-syllabus-print-root] .print-rich-text ol {
          padding-left: 24px;
        }
        [data-syllabus-print-root] details {
          display: block !important;
          margin: 8px 0;
        }
        [data-syllabus-print-root] details > * {
          display: block !important;
        }
        [data-syllabus-print-root] .accordion-content,
        [data-syllabus-print-root] [data-accordion-content] {
          display: block !important;
          max-height: none !important;
          height: auto !important;
          overflow: visible !important;
          opacity: 1 !important;
          visibility: visible !important;
        }
        [data-syllabus-print-root] .print-schedule-row {
          display: grid;
          grid-template-columns: 118px 1fr;
          gap: 14px;
          padding: 10px 0;
          border-bottom: 1px solid #e5eaee;
          break-inside: avoid;
        }
        [data-syllabus-print-root] .print-schedule-week {
          color: #627d98;
          font-size: 12px;
        }
        [data-syllabus-print-root] .print-schedule-body {
          min-width: 0;
        }
        [data-syllabus-print-root] .print-schedule-item {
          margin-bottom: 4px;
        }
        [data-syllabus-print-root] .print-holiday {
          color: #a12a2a;
        }
        [data-syllabus-print-root] .print-meeting {
          color: #5a4383;
        }
        [data-syllabus-print-root] .print-assignment {
          color: #2f6f9f;
        }
        [data-syllabus-print-root] .print-schedule-description {
          color: #627d98;
          margin-top: 5px;
        }
        [data-syllabus-print-root] .print-schedule-reading {
          color: #243b53;
          margin-top: 5px;
        }
        [data-syllabus-print-root] .print-ai-permission {
          margin: 0 0 10px;
          break-inside: avoid;
        }
        [data-syllabus-print-root] .print-note {
          color: #627d98;
          font-style: italic;
          margin-top: 3px;
        }
        [data-syllabus-print-root] img {
          max-width: 100% !important;
          height: auto !important;
        }
        [data-syllabus-print-root] table {
          width: 100% !important;
          border-collapse: collapse;
        }
        [data-syllabus-print-root] th,
        [data-syllabus-print-root] td {
          border: 1px solid #d9e2ec;
          padding: 6px;
          vertical-align: top;
        }
      </style>

      <h1>${escapeHtml(courseName)} Syllabus</h1>
      ${!wholeSyllabus && sectionsToPrint[0]
        ? `<div class="print-subtitle">${escapeHtml(
            sectionsToPrint[0].name
          )}</div>`
        : ''}
      ${sectionsToPrint.map(buildPrintableSectionHtml).join('')}
    `;

    root.querySelectorAll('details').forEach(details => {
      details.open = true;
      details.setAttribute('open', '');
    });

    root.querySelectorAll('[aria-expanded="false"]').forEach(control => {
      control.setAttribute('aria-expanded', 'true');
    });

    root.querySelectorAll('[hidden]').forEach(element => {
      element.removeAttribute('hidden');
    });

    root.querySelectorAll(
      '.accordion-content, [data-accordion-content]'
    ).forEach(element => {
      element.style.display = 'block';
      element.style.maxHeight = 'none';
      element.style.height = 'auto';
      element.style.overflow = 'visible';
      element.style.opacity = '1';
      element.style.visibility = 'visible';
    });

    return root;
  }

  async function waitForPrintableAssets(root) {
    if (document.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch {
        // Font readiness is helpful, but not required.
      }
    }

    const images = Array.from(root.querySelectorAll('img'));

    await Promise.all(
      images.map(image => {
        if (image.complete) return Promise.resolve();

        return new Promise(resolve => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        });
      })
    );

    await new Promise(resolve =>
      window.requestAnimationFrame(() =>
        window.requestAnimationFrame(resolve)
      )
    );
  }

  async function savePdfCopyToDrive(blob, fileName) {
    if (!API_URL) {
      throw new Error('REACT_APP_API_URL is not configured.');
    }

    const base64Data = await blobToBase64(blob);

    await fetch(API_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'saveSyllabusPdf',
        fileName,
        base64Data,
        mimeType: 'application/pdf'
      })
    });
  }

  async function handlePrintSyllabus(sectionToPrint = null) {
    if (printing) return;

    const sectionsToPrint = sectionToPrint
      ? [sectionToPrint]
      : displaySections;

    if (!sectionsToPrint.length) return;

    const wholeSyllabus = !sectionToPrint;
    const courseName = safePdfFilePart(
      course.name ||
      course.course_titles?.name ||
      'Course'
    );
    const sectionSuffix = sectionToPrint
      ? ` - ${safePdfFilePart(sectionToPrint.name)}`
      : '';

    const fileName =
      `${courseName} Syllabus${sectionSuffix}.pdf`;

    let root = null;

    setPrinting(true);

    try {
      root = createPrintableRoot(
        sectionsToPrint,
        wholeSyllabus
      );

      await waitForPrintableAssets(root);

      const blob =
        await downloadSyllabusPdfRoot(
          root,
          fileName
        );

      await savePdfCopyToDrive(
        blob,
        fileName
      );
    } catch (err) {
      console.error('Syllabus PDF generation failed:', err);
      window.alert(
        err?.message ||
        'Could not generate the syllabus PDF.'
      );
    } finally {
      if (root?.parentNode) {
        root.parentNode.removeChild(root);
      }
      setPrinting(false);
    }
  }

  async function handleSignContract(event) {
    event.preventDefault();

    if (contractSaving || contractSigned) {
      return;
    }

    setContractError('');

    const enteredName =
      String(contractNameInput || '').trim();

    if (!enteredName) {
      setContractError(
        'Please type your name to sign.'
      );
      return;
    }

    if (
      enteredName.toLowerCase() !==
      String(studentName).trim().toLowerCase()
    ) {
      setContractError(
        `Please type your name exactly as it appears: ${studentName}`
      );
      return;
    }

    setContractSaving(true);

    try {
      const completedAt =
        await markContractComplete(
          session.studentId,
          course.id,
          enteredName
        );

      setContractSigned(true);
      setContractSignedName(enteredName);
      setContractSignedAt(completedAt);
      setContractError('');
    } catch (err) {
      console.error(
        'Failed to sign Academic Integrity Contract:',
        err
      );

      setContractError(
        err?.message ||
        'Failed to save your signature. Please try again.'
      );
    } finally {
      setContractSaving(false);
    }
  }

  async function handleNext() {
    const displaySections = getDisplaySections();
    const section = displaySections[currentIndex];

    if (
      isAcademicIntegrityContractSection(
        section
      ) &&
      !contractSigned
    ) {
      setContractError(
        'Please sign the Academic Integrity Contract before continuing.'
      );
      return;
    }

    if (
      section.has_quiz &&
      section.syllabus_quiz_questions?.length >
        0
    ) {
      const questions =
        section.syllabus_quiz_questions;

      for (
        let questionIndex = 0;
        questionIndex <
        questions.length;
        questionIndex += 1
      ) {
        const question =
          questions[questionIndex];

        const choices = (() => {
          try {
            return JSON.parse(
              question.choices ||
              '[]'
            );
          } catch {
            return [];
          }
        })();

        if (choices.length === 0) {
          continue;
        }

        const answer =
          quizAnswers[
            questionIndex
          ];

        if (
          answer === undefined ||
          answer === '' ||
          Number(answer) !==
            Number(
              question
                .correct_answer_index
            )
        ) {
          setQuizError(
            `Question ${
              questionIndex + 1
            } is not correct. Please try again.`
          );

          return;
        }
      }
    }

    if (currentIndex < displaySections.length - 1) {
      setQuizAnswers({});
      setQuizError('');

      if (
        document.activeElement &&
        typeof document.activeElement.blur === 'function'
      ) {
        document.activeElement.blur();
      }

      setCurrentIndex(prev => prev + 1);
    } else {
      await markSyllabusComplete(session.studentId, course.id);
      onOnboardingUpdate('syllabus_completed');
    }
  }

function openReviewSection(section) {
  setSelectedReviewSection(section);

  if (reviewBackRef) {
    reviewBackRef.current = () => {
      setSelectedReviewSection(null);
    };
  }
}

  if (loading) return <div style={styles.wrap}><div style={styles.muted}>Loading...</div></div>;

  const displaySections = getDisplaySections();

  // REVIEW MODE — clickable section list
  if (isReview) {
    if (selectedReviewSection) {
      const section = selectedReviewSection;
      const content = getSectionContent(section);
      return (
        <div style={styles.wrap}>
          <div style={styles.content}>
            <div
  style={{
    ...styles.reviewTopBar,
    justifyContent:
      reviewBackRef
        ? 'flex-end'
        : 'space-between'
  }}
>
  {!reviewBackRef && (
    <button
      style={styles.backLink}
      onClick={() =>
        setSelectedReviewSection(null)
      }
    >
      ← Back to Syllabus
    </button>
  )}

  <button
    type="button"
    style={styles.printBtn}
    disabled={printing}
    onClick={() =>
      handlePrintSyllabus(section)
    }
  >
    {printing
      ? 'Preparing PDF...'
      : 'Print'}
  </button>
</div>
            <h2 style={styles.sectionTitle}>{section.name}</h2>
            {section.is_teaching_schedule ? (
              <TeachingSchedule
                course={course}
                weeks={weeks}
                holidays={holidays}
                meetings={meetings}
                courseAssignments={courseAssignments}
                weekReadings={weekReadings}
              />
            ) : (
              <>
                {content && (
                  <div
                    dangerouslySetInnerHTML={{ __html: substituteVariables(content, course, attendanceTableHtml) }}
                    style={styles.sectionContent}
                  />
                )}
                {isAiPermissionsSection(section) && (
                  <AiPermissionsList aiPermissions={aiPermissions} />
                )}
              </>
            )}
          </div>
        </div>
      );
    }

    return (
      <div style={styles.wrap}>
        <div style={styles.content}>
          <div style={styles.reviewMainHeader}>
            <h2 style={styles.heading}>Syllabus</h2>
            <button
              type="button"
              style={styles.printBtn}
              disabled={printing}
              onClick={() => handlePrintSyllabus()}
            >
              {printing ? 'Preparing PDF...' : 'Print'}
            </button>
          </div>
          <div style={styles.reviewList}>
            {displaySections.map((section, i) => (
              <button
                key={section.id}
                style={styles.reviewItem}
                onClick={() => openReviewSection(section)}
              >
                <span style={styles.reviewNum}>{i + 1}</span>
                <span style={styles.reviewName}>{section.name}</span>
                <span style={styles.reviewArrow}>›</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // WALKTHROUGH MODE
  const section = displaySections[currentIndex];
  if (!section) return null;

  const content = getSectionContent(section);
  const isAcademicIntegrityContract =
    isAcademicIntegrityContractSection(
      section
    );
  const quizQuestions =
    section.has_quiz
      ? (
          section
            .syllabus_quiz_questions ||
          []
        )
      : [];
  const isLast = currentIndex === displaySections.length - 1;
  const progress = Math.round((currentIndex / displaySections.length) * 100);

  return (
    <div ref={walkthroughTopRef} style={styles.wrap}>
      <div style={styles.content}>
        <div style={styles.progressBar}>
          <div style={{ ...styles.progressFill, width: `${progress}%` }} />
        </div>
        <div style={styles.walkthroughTopRow}>
          <div style={styles.progressText}>
            Section {currentIndex + 1} of {displaySections.length}
          </div>
          <button
            type="button"
            style={styles.printBtn}
            disabled={printing}
            onClick={() => handlePrintSyllabus(section)}
          >
            {printing ? 'Preparing PDF...' : 'Print'}
          </button>
        </div>

        <div
          key={section.id}
          style={styles.sectionCard}
        >
          <h2 style={styles.sectionTitle}>{section.name}</h2>

          {section.is_teaching_schedule ? (
            <TeachingSchedule
              course={course}
              weeks={weeks}
              holidays={holidays}
              meetings={meetings}
              courseAssignments={courseAssignments}
              weekReadings={weekReadings}
            />
          ) : (
            <>
              {content && (
                <StableRichText
                  html={substituteVariables(
                    content,
                    course,
                    attendanceTableHtml
                  )}
                  style={styles.sectionContent}
                />
              )}
              {isAiPermissionsSection(section) && (
                <AiPermissionsList aiPermissions={aiPermissions} />
              )}

              {isAcademicIntegrityContract && (
                <div style={styles.contractBox}>
                  {contractSigned ? (
                    <div style={styles.contractSigned}>
                      ✓ Signed
                      {contractSignedName
                        ? ` as ${contractSignedName}`
                        : ''}
                      {contractSignedAt
                        ? ` on ${new Date(
                            contractSignedAt
                          ).toLocaleDateString(
                            'en-GB',
                            {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            }
                          )}`
                        : ''}
                    </div>
                  ) : (
                    <form
                      onSubmit={handleSignContract}
                      style={styles.contractForm}
                    >
                      <p style={styles.contractInstruction}>
                        Type your name exactly as it appears (
                        <strong>{studentName}</strong>) to sign:
                      </p>

                      <input
                        type="text"
                        value={contractNameInput}
                        onChange={event => {
                          setContractNameInput(
                            event.target.value
                          );
                          setContractError('');
                        }}
                        placeholder="Your full name"
                        style={styles.contractInput}
                        disabled={contractSaving}
                      />

                      {contractError && (
                        <div style={styles.contractError}>
                          {contractError}
                        </div>
                      )}

                      <button
                        type="submit"
                        style={styles.contractSignBtn}
                        disabled={contractSaving}
                      >
                        {contractSaving
                          ? 'Signing...'
                          : 'Sign Academic Integrity Contract'}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </>
          )}

          {quizQuestions.length > 0 && (
            <div style={styles.quizBox}>
              {quizQuestions.map(
                (quiz, questionIndex) => {
                  const choices = (() => {
                    try {
                      return JSON.parse(
                        quiz.choices || '[]'
                      );
                    } catch {
                      return [];
                    }
                  })();

                  if (
                    choices.length === 0
                  ) {
                    return null;
                  }

                  return (
                    <div
                      key={`${section.id}-${quiz.id || questionIndex}`}
                      style={{
                        marginBottom:
                          questionIndex <
                          quizQuestions.length -
                            1
                            ? '20px'
                            : 0
                      }}
                    >
                      <div
                        style={
                          styles.quizQuestion
                        }
                      >
                        {quizQuestions.length >
                        1
                          ? `Question ${
                              questionIndex +
                              1
                            }: `
                          : ''}
                        {quiz.question}
                      </div>

                      <div
                        style={
                          styles.quizChoices
                        }
                      >
                        {choices.map(
                          (
                            choice,
                            choiceIndex
                          ) => (
                            <button
                              key={`${section.id}-${quiz.id || questionIndex}-${choiceIndex}`}
                              type="button"
                              style={{
                                ...styles.quizChoice,
                                ...(quizAnswers[
                                  questionIndex
                                ] ===
                                String(
                                  choiceIndex
                                )
                                  ? styles.quizChoiceSelected
                                  : {})
                              }}
                              onClick={() => {
                                setQuizAnswers(
                                  prev => ({
                                    ...prev,
                                    [questionIndex]:
                                      String(
                                        choiceIndex
                                      )
                                  })
                                );

                                setQuizError(
                                  ''
                                );
                              }}
                            >
                              {choice}
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  );
                }
              )}

              {quizError && (
                <div
                  style={styles.quizError}
                >
                  {quizError}
                </div>
              )}
            </div>
          )}

          <div style={styles.footer}>
            {cooldownRemaining > 0 ? (
              <div style={styles.cooldown}>
                Please wait {Math.floor(cooldownRemaining / 60) > 0
                  ? `${Math.floor(cooldownRemaining / 60)}m ${cooldownRemaining % 60}s`
                  : `${cooldownRemaining}s`} before continuing
              </div>
            ) : (
              isAcademicIntegrityContract &&
              !contractSigned
            ) ? (
              <div style={styles.contractContinueNotice}>
                Sign the Academic Integrity Contract above to continue.
              </div>
            ) : (
              <button style={styles.nextBtn} onClick={handleNext}>
                {isLast ? 'Complete Syllabus' : 'Continue →'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrap: { minHeight: 0, background: 'transparent' },
  content: { padding: 0, maxWidth: 'none', margin: 0 },
  muted: { color: '#627d98', fontSize: '14px', padding: '20px' },
  heading: { margin: '0 0 20px', fontSize: '26px', color: '#243b53', fontWeight: '700' },
  backLink: {
    border: 'none', background: 'none', color: '#2f6f9f',
    fontSize: '14px', fontWeight: '600', cursor: 'pointer',
    padding: '0', display: 'block'
  },
  reviewTopBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '16px'
  },
  reviewMainHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '16px'
  },
  walkthroughTopRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '14px'
  },
  printBtn: {
    background: 'white',
    color: '#2f6f9f',
    border: '1px solid #c5d6e3',
    borderRadius: '5px',
    padding: '8px 13px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    flexShrink: 0
  },
  progressBar: { height: '4px', background: '#d9e2ec', borderRadius: '2px', marginBottom: '8px' },
  progressFill: { height: '100%', background: '#2f6f9f', borderRadius: '2px', transition: 'width 0.3s' },
  progressText: { fontSize: '12px', color: '#627d98', margin: 0, textAlign: 'right' },
  sectionCard: {
    background: 'white', borderRadius: '6px', padding: '24px',
    border: '1px solid #d9e2ec', boxShadow: 'none'
  },
  sectionTitle: { margin: '0 0 16px', fontSize: '20px', color: '#243b53', fontWeight: '700' },
  sectionContent: { fontSize: '15px', lineHeight: '1.7', color: '#243b53' },
  quizBox: {
    marginTop: '24px', padding: '16px', background: '#f7f8fa',
    borderRadius: '6px', border: '1px solid #d9e2ec'
  },
  quizQuestion: { fontWeight: '700', fontSize: '15px', color: '#243b53', marginBottom: '12px' },
  quizChoices: { display: 'flex', flexDirection: 'column', gap: '8px' },
  quizChoice: {
    width: '100%', textAlign: 'left', padding: '12px 16px',
    border: '2px solid #d9e2ec', borderRadius: '5px', background: 'white',
    fontSize: '15px', cursor: 'pointer', color: '#243b53',
    outline: 'none', boxShadow: 'none'
  },
  quizChoiceSelected: { borderColor: '#2f6f9f', background: '#eef5fa', color: '#2f6f9f', fontWeight: '600' },
  quizError: { color: '#a12a2a', fontSize: '13px', marginTop: '8px' },
  footer: { marginTop: '24px', display: 'flex', justifyContent: 'flex-end' },
  cooldown: { fontSize: '14px', color: '#627d98', fontStyle: 'italic', padding: '10px 0' },
  nextBtn: {
    background: '#2f6f9f', color: 'white', border: 'none', borderRadius: '5px',
    padding: '12px 24px', fontSize: '15px', fontWeight: '600', cursor: 'pointer'
  },
  contractBox: {
    marginTop: '24px',
    padding: '18px',
    background: '#f7f8fa',
    border: '1px solid #d9e2ec',
    borderRadius: '6px'
  },
  contractForm: {
    margin: 0
  },
  contractInstruction: {
    margin: '0 0 12px',
    color: '#243b53',
    fontSize: '14px',
    lineHeight: '1.5'
  },
  contractInput: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #d9e2ec',
    borderRadius: '5px',
    padding: '10px 12px',
    fontSize: '15px',
    marginBottom: '12px'
  },
  contractError: {
    background: '#faeaea',
    border: '1px solid #e8bcbc',
    borderRadius: '5px',
    padding: '10px 14px',
    color: '#a12a2a',
    fontSize: '14px',
    marginBottom: '12px'
  },
  contractSignBtn: {
    background: '#2f6f9f',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    padding: '11px 18px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  contractSigned: {
    background: '#e7f5ed',
    border: '1px solid #b9dfc9',
    borderRadius: '5px',
    padding: '12px 14px',
    color: '#216e45',
    fontSize: '14px',
    fontWeight: '600'
  },
  contractContinueNotice: {
    fontSize: '14px',
    color: '#627d98',
    fontStyle: 'italic',
    padding: '10px 0'
  },
  reviewList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  reviewItem: {
    width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 16px', background: 'white', border: '1px solid #d9e2ec',
    borderRadius: '6px', cursor: 'pointer', textAlign: 'left'
  },
  reviewNum: {
    background: '#eef5fa', color: '#2f6f9f', borderRadius: '50%',
    width: '28px', height: '28px', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: '13px', fontWeight: '700', flexShrink: 0
  },
  reviewName: { flex: 1, fontWeight: '600', fontSize: '15px', color: '#243b53' },
  reviewArrow: { color: '#627d98', fontSize: '20px', flexShrink: 0 },
  aiPermissions: { marginTop: '16px' },
  aiSection: { marginBottom: '16px' },
  aiHeading: { fontWeight: '700', fontSize: '15px', color: '#216e45', marginBottom: '10px' },
  aiCategory: { marginBottom: '10px' },
  aiCategoryName: { fontWeight: '700', fontSize: '13px', color: '#627d98', marginBottom: '4px' },
  aiPermissionItem: { marginBottom: '10px' },
  aiType: { fontSize: '14px', color: '#243b53', padding: '2px 0', fontWeight: '600' },
  aiDescription: { fontSize: '13px', color: '#627d98', lineHeight: '1.5', paddingLeft: '14px', marginTop: '2px' }
};

const scheduleStyles = {
  wrap: { display: 'flex', flexDirection: 'column', gap: '8px' },
  weekRow: {
    display: 'flex', gap: '16px', padding: '12px',
    background: '#f7f8fa', borderRadius: '5px', border: '1px solid #d9e2ec'
  },
  weekLeft: { width: '160px', flexShrink: 0 },
  weekNum: { fontWeight: '700', fontSize: '13px', color: '#2f6f9f' },
  weekDate: { fontSize: '12px', color: '#627d98', marginTop: '2px' },
  weekRight: { flex: 1, fontSize: '14px', color: '#243b53', lineHeight: '1.6' },
  holiday: { color: '#a12a2a', fontWeight: '600', marginBottom: '4px' },
  meeting: { color: '#2f6f9f', marginBottom: '4px' },
  due: { color: '#8a5b00', marginBottom: '4px' },
  description: { color: '#243b53', fontSize: '14px', lineHeight: '1.5' },
  reading: {
    color: '#243b53', fontSize: '14px', lineHeight: '1.5',
    marginTop: '6px'
  },
  readingLink: {
    display: 'block', color: '#2f6f9f', fontSize: '14px',
    lineHeight: '1.5', marginTop: '6px', textDecoration: 'none'
  }
};